// Detects when a chat input is a single IPv4 or IPv6 address — the trigger for
// the IP search panel. Tolerates an optional `/ip ` quick-action prefix so the
// function is self-contained even though ChatPrompt also strips it before
// sending. Both address families route to the same `ip:<value>` target, which
// hits the same /ui/search-ip endpoint on the backend.
import { removeBrackets } from './detectDomain';

const OCTET = '(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';
const IPV4 = new RegExp(`^${OCTET}\\.${OCTET}\\.${OCTET}\\.${OCTET}$`);

// Covers every RFC 4291 form: full 8-group, `::` compression at any position,
// and IPv4-embedded suffixes (e.g. `::ffff:192.0.2.1`). Doesn't attempt to
// validate zone-id suffixes (`%eth0`) — the backend doesn't accept them either.
const H16 = '[0-9a-fA-F]{1,4}';
const IPV4_TAIL = `(?:${OCTET}\\.){3}${OCTET}`;
const IPV6 = new RegExp(
  '^(?:' +
    `(?:${H16}:){7}${H16}` +
    `|(?:${H16}:){1,7}:` +
    `|(?:${H16}:){1,6}:${H16}` +
    `|(?:${H16}:){1,5}(?::${H16}){1,2}` +
    `|(?:${H16}:){1,4}(?::${H16}){1,3}` +
    `|(?:${H16}:){1,3}(?::${H16}){1,4}` +
    `|(?:${H16}:){1,2}(?::${H16}){1,5}` +
    `|${H16}:(?::${H16}){1,6}` +
    `|:(?::${H16}){1,7}` +
    `|(?:${H16}:){6}${IPV4_TAIL}` +
    `|(?:${H16}:){0,5}:${IPV4_TAIL}` +
    `|::(?:ffff(?::0{1,4})?:)?${IPV4_TAIL}` +
    ')$',
);

export function extractSingleIp(text: string): string | null {
  // Undefang first (185.95.156[.]53 → 185.95.156.53) so defanged IPs open the
  // panel too, matching the domain path.
  const cleaned = removeBrackets(text.trim().replace(/^\/ip\s+/i, '')).trim();
  if (IPV4.test(cleaned)) return cleaned;
  if (IPV6.test(cleaned)) return cleaned;
  return null;
}

/** True when `s` is a bare IPv4 or IPv6 address (same grammar as extractSingleIp).
 *  Use this when you want the classification only — don't call extractSingleIp
 *  as a boolean, its `/ip ` prefix stripping and trimming can mislead the reader. */
export const isIp = (s: string): boolean => extractSingleIp(s) != null;
